import type { InventoryRow, OrderRow, PricingRow } from '../../api/amazonconnect';
import type { WindowAggregate } from '../daily-brief/aggregateOrders';
import { windowAggregate } from '../daily-brief/aggregateOrders';
import type { DayWindow } from '../daily-brief/dayWindows';
import { pctChange } from '../daily-brief/format';
import type { BusinessHealth } from '../daily-brief/types';
import type { ExecutiveKpi, ExecutiveKpiStripModel, KpiStatus } from './types';

function statusAvailable(
  value: number | null,
  pct: number | null,
  higherIsBetter: boolean,
  hasCompare: boolean,
): KpiStatus {
  if (value == null) return 'na';
  if (!hasCompare || pct == null) return 'good';
  return statusFromPct(pct, higherIsBetter);
}

function statusFromPct(pct: number | null, higherIsBetter: boolean): KpiStatus {
  if (pct == null || !Number.isFinite(pct)) return 'na';
  const v = higherIsBetter ? pct : -pct;
  if (v >= 5) return 'good';
  if (v >= -10) return 'watch';
  return 'risk';
}

function statusFromScore(score: number | null): KpiStatus {
  if (score == null) return 'na';
  if (score >= 80) return 'good';
  if (score >= 50) return 'watch';
  return 'risk';
}

function confTotals(agg: WindowAggregate): number {
  if (agg.orderCount === 0) return 70;
  return Math.round(100 * (agg.ordersWithTotal / agg.orderCount));
}

function confUnits(agg: WindowAggregate): number {
  if (agg.orderCount === 0) return 70;
  return Math.round(100 * (agg.ordersWithUnits / agg.orderCount));
}

function aspOf(agg: WindowAggregate): number | null {
  if (agg.sales == null || agg.units == null || agg.units <= 0) return null;
  return agg.sales / agg.units;
}

function qs(
  connectionId: string,
  from: string,
  to: string,
  path: string,
  extra = '',
): string {
  const base = `${path}?connection=${encodeURIComponent(connectionId)}&from=${from}&to=${to}`;
  return extra ? `${base}${extra.startsWith('&') ? extra : `&${extra}`}` : base;
}

/** Map daily aggregates into sparkline series for metric extractors. */
export function sparklineSeries(
  orders: OrderRow[],
  days: DayWindow[],
  pick: (a: WindowAggregate) => number | null,
): number[] {
  const points: number[] = [];
  for (const d of days) {
    const v = pick(windowAggregate(orders, d));
    if (v != null && Number.isFinite(v)) points.push(v);
  }
  return points.length >= 2 ? points : [];
}

export function sparklineSeriesFromAggs(
  days: WindowAggregate[],
  pick: (a: WindowAggregate) => number | null,
): number[] {
  const points: number[] = [];
  for (const a of days) {
    const v = pick(a);
    if (v != null && Number.isFinite(v)) points.push(v);
  }
  return points.length >= 2 ? points : [];
}

export function inventoryAvailable(rows: InventoryRow[]): {
  sum: number | null;
  sample: number;
} {
  if (!rows.length) return { sum: null, sample: 0 };
  let sum = 0;
  for (const r of rows) {
    const a = typeof r.available === 'number' ? r.available : null;
    if (a == null) return { sum: null, sample: rows.length };
    sum += a;
  }
  return { sum, sample: rows.length };
}

/** Buy Box share among pricing rows that expose LIVE winner flags. */
export function buyBoxShare(rows: PricingRow[]): {
  value: number | null;
  scored: number;
  sample: number;
  reason?: string;
} {
  if (!rows.length) {
    return { value: null, scored: 0, sample: 0, reason: 'No LIVE pricing rows in sample' };
  }
  let scored = 0;
  let wins = 0;
  let pctSum = 0;
  let pctN = 0;
  for (const r of rows) {
    if (typeof r.buy_box_winner === 'boolean') {
      scored += 1;
      if (r.buy_box_winner) wins += 1;
    }
    if (r.buy_box_pct != null && String(r.buy_box_pct).trim() !== '') {
      const n = Number(String(r.buy_box_pct).replace(/[^0-9.+-eE]/g, ''));
      if (Number.isFinite(n)) {
        pctN += 1;
        pctSum += n;
      }
    }
  }
  if (scored > 0) {
    return { value: (wins / scored) * 100, scored, sample: rows.length };
  }
  if (pctN > 0) {
    return {
      value: pctSum / pctN,
      scored: pctN,
      sample: rows.length,
      reason: 'Average of buy_box_pct when winner flag absent',
    };
  }
  return {
    value: null,
    scored: 0,
    sample: rows.length,
    reason: 'Buy Box fields not present on pricing sample',
  };
}

export function buildExecutiveKpis(input: {
  connectionId: string | null;
  current: DayWindow;
  prior: DayWindow | null;
  orders: OrderRow[];
  inventory: InventoryRow[];
  pricing: PricingRow[];
  health: BusinessHealth;
  sparkDays: DayWindow[];
  truncated: boolean;
  periodLabel: string;
  priorLabel: string;
  error: string | null;
  /** When set, GMV / items / units come from Gate A (same as Yesterday). */
  currentAgg?: WindowAggregate;
  priorAgg?: WindowAggregate;
  sparkAggs?: WindowAggregate[];
  moneySource?: string;
}): ExecutiveKpiStripModel {
  const conn = input.connectionId ?? '';
  const cur = input.current;
  const emptyAgg: WindowAggregate = {
    orderCount: 0,
    ordersWithTotal: 0,
    sales: null,
    currencies: [],
    units: null,
    ordersWithUnits: 0,
    unshipped: 0,
    canceled: 0,
    aov: null,
  };
  const y = input.currentAgg ?? (conn ? windowAggregate(input.orders, cur) : emptyAgg);
  const p = input.priorAgg ?? (conn && input.prior ? windowAggregate(input.orders, input.prior) : emptyAgg);
  const inv = inventoryAvailable(input.inventory);
  const bb = buyBoxShare(input.pricing);
  const from = cur.isoFrom;
  const to = cur.isoTo;
  const ccy = y.currencies[0];
  const moneyNote = input.moneySource
    ? ` Same source as Yesterday (${input.moneySource}).`
    : '';

  const salesSpark = input.sparkAggs
    ? sparklineSeriesFromAggs(input.sparkAggs, (a) => a.sales)
    : sparklineSeries(input.orders, input.sparkDays, (a) => a.sales);
  const ordersSpark = input.sparkAggs
    ? sparklineSeriesFromAggs(input.sparkAggs, (a) => a.orderCount)
    : sparklineSeries(input.orders, input.sparkDays, (a) => a.orderCount);
  const unitsSpark = input.sparkAggs
    ? sparklineSeriesFromAggs(input.sparkAggs, (a) => a.units)
    : sparklineSeries(input.orders, input.sparkDays, (a) => a.units);
  const aovSpark = input.sparkAggs
    ? sparklineSeriesFromAggs(input.sparkAggs, (a) => a.aov)
    : sparklineSeries(input.orders, input.sparkDays, (a) => a.aov);
  const aspSpark = input.sparkAggs
    ? sparklineSeriesFromAggs(input.sparkAggs, (a) => aspOf(a))
    : sparklineSeries(input.orders, input.sparkDays, (a) => aspOf(a));

  const trimConf = (base: number) =>
    input.truncated ? Math.max(0, base - 15) : base;

  const ordersHref = conn ? qs(conn, from, to, '/amazon/orders') : '/amazon/connect';
  const invHref = conn
    ? `/amazon/inventory?connection=${encodeURIComponent(conn)}`
    : '/amazon/connect';
  const pricingHref = conn
    ? `/amazon/pricing?connection=${encodeURIComponent(conn)}`
    : '/amazon/connect';
  const connectHref = conn
    ? `/amazon/connect?connection=${encodeURIComponent(conn)}`
    : '/amazon/connect';

  const hasCompare = Boolean(input.prior);
  const salesPct = hasCompare ? pctChange(y.sales, p.sales) : null;
  const ordersPct = hasCompare ? pctChange(y.orderCount, p.orderCount) : null;
  const unitsPct = hasCompare ? pctChange(y.units, p.units) : null;
  const aovPct = hasCompare ? pctChange(y.aov, p.aov) : null;
  const aspPct = hasCompare ? pctChange(aspOf(y), aspOf(p)) : null;

  const kpis: ExecutiveKpi[] = [
    {
      id: 'gmv',
      label: 'Gross Sales (GMV)',
      format: 'money',
      accent: 'var(--shell-accent)',
      value: y.sales,
      prior: hasCompare ? p.sales : null,
      pctChange: salesPct,
      sparkline: salesSpark,
      status: statusAvailable(y.sales, salesPct, true, hasCompare),
      confidence: trimConf(confTotals(y)),
      currency: ccy,
      href: ordersHref,
      tooltip:
        'Sum of item-price on All Orders lines in the selected period (Gate A). Pending/COD included. Multi-currency windows are not mixed.' +
        moneyNote,
      reason:
        y.sales == null
          ? y.currencies.length > 1
            ? 'Multiple currencies — GMV not mixed'
            : y.orderCount > 0
              ? 'OrderTotal missing on headers'
              : conn
                ? undefined
                : 'No connection'
          : y.ordersWithTotal < y.orderCount
            ? `${y.ordersWithTotal}/${y.orderCount} orders have totals`
            : undefined,
    },
    {
      id: 'orders',
      label: 'Order items',
      format: 'int',
      accent: '#38bdf8',
      value: conn ? y.orderCount : null,
      prior: hasCompare && conn ? p.orderCount : null,
      pctChange: conn ? ordersPct : null,
      sparkline: ordersSpark,
      status: statusAvailable(conn ? y.orderCount : null, ordersPct, true, hasCompare),
      confidence: trimConf(conn ? 95 : 0),
      href: ordersHref,
      tooltip: 'Count of All Orders lines (Seller Central Order items). Same as Yesterday.',
      reason: conn ? undefined : 'No connection',
    },
    {
      id: 'units',
      label: 'Units Sold',
      format: 'int',
      accent: '#a78bfa',
      value: y.units,
      prior: hasCompare ? p.units : null,
      pctChange: unitsPct,
      sparkline: unitsSpark,
      status: statusAvailable(y.units, unitsPct, true, hasCompare),
      confidence: trimConf(confUnits(y)),
      href: ordersHref,
      tooltip: 'Sum of quantity on All Orders lines (cancelled excluded). Same as Yesterday.',
      reason:
        y.units == null
          ? y.orderCount > 0
            ? 'Header quantity absent on orders in window'
            : conn
              ? undefined
              : 'No connection'
          : undefined,
    },
    {
      id: 'aov',
      label: 'Average Order Value',
      format: 'money',
      accent: '#34d399',
      value: y.aov,
      prior: hasCompare ? p.aov : null,
      pctChange: aovPct,
      sparkline: aovSpark,
      status: statusAvailable(y.aov, aovPct, true, hasCompare),
      confidence: trimConf(confTotals(y)),
      currency: ccy,
      href: ordersHref,
      tooltip: 'GMV ÷ order items in the period (same formula as Yesterday).',
      reason: y.aov == null ? 'Requires OrderTotal on headers' : undefined,
    },
    {
      id: 'asp',
      label: 'Average Selling Price',
      format: 'money',
      accent: '#fbbf24',
      value: aspOf(y),
      prior: hasCompare ? aspOf(p) : null,
      pctChange: aspPct,
      sparkline: aspSpark,
      status: statusAvailable(aspOf(y), aspPct, true, hasCompare),
      confidence: trimConf(Math.min(confTotals(y), confUnits(y))),
      currency: ccy,
      href: ordersHref,
      tooltip: 'GMV ÷ units sold when both GMV and units are present (All Orders).',
      reason: aspOf(y) == null ? 'Needs GMV and units' : undefined,
    },
    {
      id: 'return_ratio',
      label: 'Return Ratio',
      format: 'pct',
      accent: '#fb7185',
      value: null,
      prior: null,
      pctChange: null,
      sparkline: [],
      status: 'na',
      confidence: 0,
      href: ordersHref,
      tooltip: 'Return units from the Amazon returns report, divided by Gate A units, only when both share a complete window.',
      reason: 'Returns report is listed on the dashboard as row counts. Ratio stays N/A until that report is window-aligned to Gate A units.',
    },
    {
      id: 'inventory_available',
      label: 'Inventory Available',
      format: 'int',
      accent: '#2dd4bf',
      value: inv.sum,
      prior: null,
      pctChange: null,
      sparkline: [],
      status: inv.sum == null ? 'na' : inv.sum === 0 ? 'risk' : 'good',
      confidence: inv.sample > 0 ? (input.inventory.length >= 100 ? 55 : 75) : 0,
      href: invHref,
      tooltip:
        'Sum of available units on the LIVE inventory page sample (first page). Not full catalog total.',
      reason:
        inv.sum == null
          ? inv.sample === 0
            ? 'Inventory sample empty or not loaded'
            : 'available field incomplete on sample rows'
          : `Sample ${inv.sample} rows`,
    },
    {
      id: 'buy_box',
      label: 'Buy Box Health',
      format: 'pct',
      accent: '#f472b6',
      value: bb.value,
      prior: null,
      pctChange: null,
      sparkline: [],
      status: bb.value == null ? 'na' : statusFromScore(bb.value),
      confidence: bb.scored > 0 ? Math.min(90, 40 + bb.scored) : 0,
      href: pricingHref,
      tooltip:
        'Share of LIVE pricing sample rows that are Buy Box winner (or avg buy_box_pct when winner flag only partially available).',
      reason: bb.reason,
    },
    {
      id: 'business_health',
      label: 'Business Health Score',
      format: 'score',
      accent: '#4ade80',
      value: input.health.overall,
      prior: null,
      pctChange: null,
      sparkline: [],
      status: statusFromScore(input.health.overall),
      confidence: input.health.confidence,
      href: connectHref,
      tooltip:
        'Composite of ready certified components (demand, fulfillment, inventory sample, platform). Scored only when ≥3 components ready.',
      reason:
        input.health.overall == null
          ? `Need ≥3 ready components (${input.health.componentsReady} ready)`
          : undefined,
    },
    {
      id: 'data_confidence',
      label: 'Data Confidence Score',
      format: 'score',
      accent: '#94a3b8',
      value: conn ? input.health.confidence : null,
      prior: null,
      pctChange: null,
      sparkline: [],
      status: !conn ? 'na' : statusFromScore(input.health.confidence),
      confidence: conn ? input.health.confidence : 0,
      href: connectHref,
      tooltip:
        'Confidence that KPI inputs are complete: field coverage + component readiness (reduced when scans truncate).',
      reason: conn ? undefined : 'No connection',
    },
  ];

  // Empty sales when orderCount=0 and conn: aggregate returns sales 0.
  // Spec 000003: never fabricated zeros. Order count 0 is real; GMV 0 empty period is real sum.
  // Leave as-is from windowAggregate.

  return {
    loadedAt: new Date().toISOString(),
    connectionId: input.connectionId,
    periodLabel: input.periodLabel,
    priorLabel: input.priorLabel,
    from,
    to,
    kpis,
    truncated: input.truncated,
    error: input.error,
  };
}
